import { create } from 'zustand'
import { useAccountMatterStore } from './use-account-matter-store';
import { sleep } from '../utils';
import { MatterModelInfo } from '../ai/matter-model-info';

type ModelInfoStore = {
  loading: boolean
  errorMessage: string
  modelInfo?: MatterModelInfo & { earliestMatterFileDateModified: number }
}

export const useModelInfoStore = create<ModelInfoStore>(() => {
  return {
    loading: true,
    errorMessage: '',
    modelInfo: undefined,
  };
});

const fetchDataWithAbort = async (signal: AbortSignal) => {
  const { accountId, matterId } = useAccountMatterStore.getState();
  if (!matterId || !accountId) {
    useModelInfoStore.setState({ loading: false, errorMessage: '' });
    return;
  }

  useModelInfoStore.setState({ loading: true, errorMessage: '' });

  try {
    const maxAttempts = 10
    let attempt = 0
    while (true) {
      if (signal.aborted) {
        return;
      }
      const response = await fetch(
        `/api/matter/model-info?matterId=${matterId}${accountId ? `&accountId=${accountId}` : ''}`,
      )
      // we don't pass signal to fetch as it creates too many errors in server logs, but exit after fetch if aborted
      if (signal.aborted) {
        return
      }
      
      if (response.status === 401) {
        if (attempt >= maxAttempts) {
          throw new Error('Unauthorized from fusion')
        }
        attempt += 1
        await sleep(3000)
        continue
      }
      if (!response.ok) {
        throw new Error('Something went wrong trying to get model info')
      }

      const modelInfo = (await response.json()) as MatterModelInfo & { earliestMatterFileDateModified: number }
      useModelInfoStore.setState({ modelInfo });
      break
    }
  } catch (err) {
    if (err instanceof Error) {
      if (err.name !== 'AbortError') {
        useModelInfoStore.setState({ errorMessage: err.message });
      }
    }
  } finally {
    if (!signal.aborted) {
      useModelInfoStore.setState({ loading: false });
    }
  }
}

let currentController: AbortController | null = null;

const fetchData = () => {
  // need to keep this in closure so we can compare after finishing.
  const closureController = new AbortController();
  if (currentController) {
    currentController.abort();
  }
  currentController = closureController;

  fetchDataWithAbort(closureController.signal).catch((err) => {
    if (err?.name !== 'AbortError') {
      console.error('[MODEL-INFO] Error fetching model info:', err);
    }
  }).finally(() => {
    if (!closureController.signal.aborted && closureController === currentController) {
      currentController = null;
    }
  })
}

// we need to subscribe to the useAccountMatterStore to see accountId/matterId changes
const subscribeToAccountId = () => {
  const unsubscribe = useAccountMatterStore.subscribe(
    (state, prevState) => {
      if (state.accountId !== prevState.accountId || state.matterId !== prevState.matterId) {
        fetchData();
      }
    }
  );

  // Return cleanup function
  return () => {
    // Abort any ongoing request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Unsubscribe from the store
    unsubscribe();
  };
}


export const initModelInfoStore = () => {
  const disableAccountSub = subscribeToAccountId();
  fetchData();
  return [disableAccountSub];
}
